import React from 'react';
import { Shield, Search, CheckCircle, BarChart3, Download, X } from 'lucide-react';

interface Props {
  onClose: () => void;
}

const WELCOME_KEY = 'nis2mapper_welcomed';

export const hasBeenWelcomed = (): boolean =>
  localStorage.getItem(WELCOME_KEY) === 'true';

export const markWelcomed = (): void =>
  localStorage.setItem(WELCOME_KEY, 'true');

const WelcomeModal: React.FC<Props> = ({ onClose }) => {
  const handleClose = () => {
    markWelcomed();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">

        {/* Header */}
        <div className="relative bg-gradient-to-r from-blue-700 to-blue-500 rounded-t-2xl p-8 text-white">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-1.5 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6" />
            </div>
            <span className="text-blue-100 text-sm font-medium uppercase tracking-wide">ComplianceHub.wiki</span>
          </div>
          <h1 className="text-2xl font-bold mb-2">EU Cybersecurity Standards Mapping Tool</h1>
          <p className="text-blue-100 text-sm leading-relaxed">
            Free, interactive tool for mapping NIS2 Directive requirements across ISO 27001, NIST CSF, ETSI, and 5 national frameworks — so you implement controls once and satisfy multiple standards.
          </p>
        </div>

        {/* Body */}
        <div className="p-8">

          {/* What it covers */}
          <div className="mb-6">
            <h2 className="text-base font-semibold text-gray-800 mb-3">What's inside</h2>
            <div className="grid grid-cols-2 gap-3">
              {[
                { num: '39', desc: 'NIS2 Article 21 requirements' },
                { num: '13', desc: 'Compliance categories' },
                { num: '4', desc: 'International frameworks (ISO 27001, NIST, ETSI, CEN)' },
                { num: '5', desc: 'National frameworks (BE, FI, GR, ES, FR)' },
              ].map(({ num, desc }) => (
                <div key={desc} className="bg-blue-50 rounded-lg p-3 flex items-start gap-3">
                  <span className="text-xl font-bold text-blue-600 leading-none">{num}</span>
                  <span className="text-sm text-gray-600">{desc}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Features */}
          <div className="mb-6">
            <h2 className="text-base font-semibold text-gray-800 mb-3">How to use it</h2>
            <div className="space-y-3">
              {[
                {
                  icon: <Search className="w-5 h-5 text-blue-600" />,
                  title: 'Search & Filter',
                  desc: 'Find any requirement by ID, title, or any framework reference (e.g. "PR.AA-03" or "A.8.5"). Filter by the 13 compliance categories.',
                },
                {
                  icon: <CheckCircle className="w-5 h-5 text-green-600" />,
                  title: 'Track your progress',
                  desc: 'Mark each requirement as Not Started, In Progress, or Completed. Your progress is saved automatically in your browser.',
                },
                {
                  icon: <BarChart3 className="w-5 h-5 text-purple-600" />,
                  title: 'Visualize compliance',
                  desc: 'Switch to Visualization view to see your completion rate by category and by framework standard.',
                },
                {
                  icon: <Download className="w-5 h-5 text-gray-600" />,
                  title: 'Export for reporting',
                  desc: 'Download filtered results as CSV or JSON to share with auditors, management, or integrate into GRC tools.',
                },
              ].map(({ icon, title, desc }) => (
                <div key={title} className="flex items-start gap-3">
                  <div className="mt-0.5 flex-shrink-0">{icon}</div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">{title}</p>
                    <p className="text-sm text-gray-500">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Requirement levels */}
          <div className="mb-8 p-4 bg-gray-50 rounded-xl">
            <p className="text-sm font-medium text-gray-700 mb-2">Requirement levels (Belgium BE-CyFun® framework):</p>
            <div className="flex flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <span className="cs-badge cs-badge-green">BASIC</span>
                <span className="text-xs text-gray-500">Foundation controls</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="cs-badge cs-badge-yellow">IMPORTANT</span>
                <span className="text-xs text-gray-500">Enhanced posture</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="cs-badge cs-badge-red">ESSENTIAL</span>
                <span className="text-xs text-gray-500">Critical — act now</span>
              </div>
            </div>
          </div>

          {/* CTA */}
          <button
            onClick={handleClose}
            className="w-full py-3.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors text-sm"
          >
            Get Started — Explore the Tool
          </button>
          <p className="text-center text-xs text-gray-400 mt-3">
            Free to use · No sign-up required · Progress saved locally in your browser
          </p>
        </div>
      </div>
    </div>
  );
};

export default WelcomeModal;
